import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import styled from "styled-components"
import NewUserModal from "~components/users/newUser"
import UserDetailHeader from "~components/users/UserDetailHeader"
import { Card } from "~components/Card"
import PATHS, { getFullPath } from "~constants/paths"
import UserService from "~services/UserService"
import { NotificationType } from "~constants/notifications"
import { UserShape } from "~shapes/user"
import colors from "~constants/colors"

interface Props {
  user: UserShape
  isLoading: boolean
  updateUser: (user: UserShape) => void
  afterDeletePath?: string
}

const UserHeaderWithActions = ({
  user,
  isLoading,
  updateUser,
  afterDeletePath,
}: Props) => {
  const navigate = useNavigate()
  const [isEditUserModalOpened, setIsEditUserModalOpened] = useState(false)
  const [isGettingUser, setIsGettingUser] = useState(false)
  const [editUser, setEditUser] = useState<UserShape>(null!)
  const [isSendingNotification, setIsSendingNotification] =
    useState<NotificationType | null>(null)

  const handleSendNotification = async (type: NotificationType) => {
    if (!user?.id) return
    setIsSendingNotification(type)

    try {
      await UserService.sendNotification(user.id, type)
      toast.success(
        `${
          type === NotificationType.WEB_LOGIN_LINK
            ? "Web login link"
            : "Reset password link"
        } sent successfully`,
      )
    } catch {
      toast.error(
        `Failed to send ${
          type === NotificationType.WEB_LOGIN_LINK
            ? "web login"
            : "reset password"
        } link`,
      )
    } finally {
      setIsSendingNotification(null)
    }
  }

  const handleUserDeleteClick = () => {
    if (!user?.id) return

    const confirmed = confirm(
      `Confirm deletion for user ${user.email}? This action is permanent.`,
    )
    if (!confirmed) return

    UserService.DELETEUser(user.id)
      .then(() => {
        toast.success("User deleted successfully.")
        navigate(afterDeletePath ?? getFullPath(PATHS.users))
      })
      .catch(() => toast.error("Error deleting user. Try again."))
  }

  const handleEditUserClick = async () => {
    if (!user?.id) return

    setIsGettingUser(true)
    setIsEditUserModalOpened(true)

    try {
      const fullUser = await UserService.getUserById(user.id)
      setEditUser(fullUser)
    } catch {
      setEditUser(user)
      toast.error(
        "Failed to load complete user data. Some fields may be missing.",
      )
    } finally {
      setIsGettingUser(false)
    }
  }

  return (
    <>
      <Card padding="16">
        <SectionLabel>User details</SectionLabel>
        <UserDetailHeader
          user={user}
          isLoading={isLoading}
          isSendingNotification={isSendingNotification}
          onEdit={handleEditUserClick}
          onDelete={handleUserDeleteClick}
          onSendNotification={handleSendNotification}
        />
      </Card>

      {isEditUserModalOpened && (
        <NewUserModal
          user={editUser}
          isGettingUser={isGettingUser}
          onRequestClose={() => {
            setEditUser(null!)
            setIsEditUserModalOpened(false)
          }}
          onUserUpdated={updateUser}
        />
      )}
    </>
  )
}

export default UserHeaderWithActions

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.gray6};
  margin-bottom: 10px;
`
